import BuildIcon from '@mui/icons-material/Build';
import SyncIcon from '@mui/icons-material/Sync';
import React, { useState } from 'react';

import {
  PoolBrainCustomerLocationDTO,
  FieldIssue,
  PoolBrainContactCustomerSchema,
  PoolBrainContactCustomerDTO
} from '_lib/api/apiTypes.shim';
import { PoolBrainCustomerResource } from '_lib/api/resources/poolBrainCustomerResource';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogButton } from '_lib/components/DialogButton/DialogButton';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { DialogContentText } from '_lib/components/DialogContentText/DialogContentText';
import { Stack } from '_lib/components/Stack/Stack';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';
import { AsyncDialog, AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';
import { reportException } from '_lib/utils/sentryService';

interface MissingCustomerLocationInfoDialogProps
  extends AsyncDialogProps<{ customerContact: PoolBrainContactCustomerDTO }> {
  customerLocation: PoolBrainCustomerLocationDTO;
  poolBrainCustomerResource: PoolBrainCustomerResource;
  issues: FieldIssue[];
}

const getFieldIssuePaths = (fieldIssues?: FieldIssue[]) =>
  fieldIssues
    ?.filter((issue) => issue.path?.[0] !== 'nonFieldErrors')
    .map((issue) => issue.path[0])
    .join(', ');

const MissingCustomerLocationInfoDialog = (props: MissingCustomerLocationInfoDialogProps) => {
  const { customerLocation, poolBrainCustomerResource, issues, onClose, titleText, ...dialogProps } = props;

  const [fieldIssues, setFieldIssues] = useState(issues);
  const [isSaving, setIsSaving] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);

  const handleSyncCustomerLocation = async () => {
    try {
      setIsSaving(true);
      const updatedCustomer = await poolBrainCustomerResource.getById({ id: customerLocation.customer.providerId });
      setIsSaving(false);

      const parsedCustomer = PoolBrainContactCustomerSchema.safeParse(updatedCustomer);

      if (!parsedCustomer.success) {
        setRefreshFailed(true);
        setFieldIssues(parsedCustomer.error.issues);
        return;
      } else {
        onClose({
          action: 'CONFIRM',
          value: {
            customerContact: {
              ...parsedCustomer.data,
              modifiedAt: parsedCustomer.data.modifiedAt.toISOString()
            }
          }
        });
      }
    } catch (err) {
      setIsSaving(false);
      reportException(err);
    }
  };

  return (
    <DialogWrapper onClose={onClose} titleText={titleText || 'Invalid Customer'} variant='error' {...dialogProps}>
      <DialogContent dividers>
        <BackdropSpinner open={isSaving} />
        <Stack>
          <DialogContentText>
            {refreshFailed && <b>Refresh failed: </b>}Customer is missing values for the following fields(s):{' '}
            <b>{fieldIssues.length ? getFieldIssuePaths(fieldIssues) : 'Unknown Field Error'}</b>
            <br></br>
            <br></br>
            If you have already fixed this data in Pool Brain, click refresh to update the customer. Otherwise please
            fix the customer in Pool Brain before clicking refresh.
          </DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <DialogButton
          color='cancel'
          sx={(theme) => ({ color: theme.palette.error.main })}
          text='Fix'
          startIcon={<BuildIcon />}
          onClick={() => {
            window.open(customerLocation.customerURL, '_blank', 'popup');
          }}
        />
        <DialogButton startIcon={<SyncIcon />} color='cancel' onClick={handleSyncCustomerLocation} text='Refresh' />
      </DialogActions>
    </DialogWrapper>
  );
};

interface MissingCustomerLocationInfoDialog
  extends AsyncDialog<{ customerContact: PoolBrainContactCustomerDTO }, MissingCustomerLocationInfoDialogProps> {}
export const useMissingCustomerLocationInfoDialog = (): MissingCustomerLocationInfoDialog =>
  useAsyncDialog(MissingCustomerLocationInfoDialog);
